import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { NestedField } from 'react-form';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import { required } from '../../../../utils/form-validation';
import {
    DEFAULT_MAX_VERIFIED_EVENT_DURATION,
    getMaxEventEndDate,
    getTimeZones,
    shouldCheckForEventDuration,
} from '../../../../utils/events';
import { DateTimePicker, Input, Select, TitleSwitch } from '../../../Form';
import Tip from '../../../Tip';
import Message from './Message';
import InfoButton from '../../../InfoButton';
import Img from '../../../Img';
import { LinkAnchor } from '../../../Links';
import PopupV3, { PopupTypes } from '../../../Popup/PopupV3';
import { CADENCE_IDS } from '../constants/recurrenceApi.constants';
import {
    generateRecurringScheduleInstances,
    validateRecurringScheduleInstances,
    getCadenceOptions,
    mapCadenceIdToFrontendCadence,
    mapFrontendCadenceToId,
    mapMomentDayToBackendDay,
    RECURRENCE_CADENCE,
    RECURRENCE_MAX_INSTANCES,
    isSingleDayEvent,
} from '../recurrenceUtils';
import { updateDivisionSpecificDatesAfterTimeZoneUpdate, updateEventScheduleDatesAfterTimeZoneUpdate } from '../utils';
import { useStatsig } from '../../../hooks/useStatsig';
import { scheduleValidate as scheduleValidateUtil, formatInstanceSchedule as formatInstanceScheduleUtil } from './scheduleUtils';

import styles from './Schedule.module.scss';

// React component — PascalCase required by React, Sonar naming rule does not apply.
function RecurrenceDatePickerPortal({ children }) { // NOSONAR
    return ReactDOM.createPortal(children, document.body);
}

const defaultRecurrenceSettings = {
    cadenceId: null,
    cadenceDayOfWeek: null,
    cadenceDayOfMonth: null,
    numberOfOccurrences: 6,
    endsOnDate: null,
    recurrentEvents: [],
    displayRecurrentEventBadge: true,
    matchRegistrationStartForAll: true,
};

const RECURRENCE_END_OPTIONS = {
    ON: 'on',
    AFTER: 'after',
};

function Schedule(props) {
    const { field, formApi, clubSettings, isHighSchoolClub, editing } = props;
    const { values } = formApi;
    const coreUITimeZones = useSelector((state) => state.coreUI.types.timeZones);
    const maxVerifiedEventDuration = clubSettings?.maxVerifiedEventDuration || DEFAULT_MAX_VERIFIED_EVENT_DURATION;

    const [schedule, setSchedule] = useState({});
    const [timezone, setTimezone] = useState(null);
    const [showTimezone, setShowTimezone] = useState(false);
    const [showDrawDates, setShowDrawDates] = useState(false);
    const [recurrenceInstances, setRecurrenceInstances] = useState([]);
    const [pendingDeleteInstance, setPendingDeleteInstance] = useState(null);
    const [editingInstanceIndex, setEditingInstanceIndex] = useState(null);
    const [instanceEditorValues, setInstanceEditorValues] = useState({
        name: '',
        eventStartUtc: null,
        eventEndUtc: null,
        registrationStartUtc: null,
        registrationEndUtc: null,
    });
    const [instanceEditorError, setInstanceEditorError] = useState(null);
    const [recurrenceEndsMode, setRecurrenceEndsMode] = useState(
        () => (values?.recurrenceSettings?.endsOnDate ? RECURRENCE_END_OPTIONS.ON : RECURRENCE_END_OPTIONS.AFTER),
    );
    const { checkGate, featureGates } = useStatsig();
    const isRecurringEventsEnabled = checkGate(featureGates.RECURRING_EVENTS);

    useEffect(() => {
        if (values?.eventSchedule) {
            const { eventType, eventSchedule } = values;
            const timeZone = eventSchedule.timeZone || 77; // 'America/Los_Angeles'
            const timeZoneObj = coreUITimeZones.find(tz => tz.id === parseInt(timeZone, 10));

            setSchedule({
                $tourDateStart: moment.utc(eventSchedule.eventStartUtc).tz(timeZoneObj.value),
                $tourDateEnd: moment.utc(eventSchedule.eventEndUtc).tz(timeZoneObj.value),
                $maxTourDateEnd: eventSchedule.maxEventEndUtc
                    ? moment.utc(eventSchedule.maxEventEndUtc).tz(timeZoneObj.value)
                    : getMaxEventEndDate(values, eventSchedule, timeZoneObj, maxVerifiedEventDuration),
                $regDateStart: moment.utc(eventSchedule.registrationStartUtc).tz(timeZoneObj.value),
                $regDateEnd: moment.utc(eventSchedule.registrationEndUtc).tz(timeZoneObj.value),
                $drawsDate: eventSchedule.drawPublishedUtc ? moment.utc(eventSchedule.drawPublishedUtc).tz(timeZoneObj.value) : null,
            });
            setTimezone(timeZone);
            setShowDrawDates(!!(eventType === 1 && eventSchedule?.drawPublishedUtc));
        } else {
            setSchedule({
                $tourDateStart: null,
                $tourDateEnd: null,
                $maxTourDateEnd: null,
                $regDateStart: null,
                $regDateEnd: null,
                $drawsDate: null,
            });
            setTimezone(77); // 'America/Los_Angeles'
            setShowDrawDates(false);
        }
    }, [values, maxVerifiedEventDuration, coreUITimeZones]);

    useEffect(() => {
        // Do not override API-loaded recurrence settings while editing
        if (editing) { return; }

        if (typeof values?.recurrenceSettings === 'undefined') {
            formApi.setValue('recurrenceSettings', defaultRecurrenceSettings);
        }
    }, [editing, formApi, editing]);

    const timeZones = getTimeZones(coreUITimeZones);
    const selectedTimeZone = timeZones.find(tz => tz.id === parseInt(timezone, 10));
    const selectedTimeZoneValue = selectedTimeZone?.value;
    const recurrenceSettings = values?.recurrenceSettings;
    const isRecurring = !!recurrenceSettings?.cadenceId;
    const recurrenceCadenceKey = mapCadenceIdToFrontendCadence(recurrenceSettings?.cadenceId);

    useEffect(() => {
        setRecurrenceEndsMode(recurrenceSettings?.endsOnDate ? RECURRENCE_END_OPTIONS.ON : RECURRENCE_END_OPTIONS.AFTER);
    }, [recurrenceSettings?.endsOnDate]);

    // Hydrate recurrenceInstances from API when editing
    useEffect(() => {
        if (!editing) { return; }

        const apiInstances = recurrenceSettings?.recurrentEvents;

        if (!apiInstances?.length || !selectedTimeZoneValue) {
            setRecurrenceInstances([]);
            return;
        }

        const primaryInstance = {
            order: 1,
            index: 0,
            name: values?.name,
            eventStartUtc: values?.eventSchedule?.eventStartUtc,
            eventEndUtc: values?.eventSchedule?.eventEndUtc,
            registrationStartUtc: values?.eventSchedule?.registrationStartUtc,
            registrationEndUtc: values?.eventSchedule?.registrationEndUtc,
            isNameOverridden: false,
        };

        const mappedInstances = apiInstances.map((event, i) => ({
            id: event.id,
            order: event.order,
            index: i + 1,
            name: event.name,
            eventStartUtc: event.startDate,
            eventEndUtc: event.endDate,
            registrationStartUtc: event.registrationStartDate,
            registrationEndUtc: event.registrationEndDate,
            isNameOverridden: event.name !== values?.name,
        }));

        setRecurrenceInstances([primaryInstance, ...mappedInstances]);
    }, [
        editing,
        recurrenceSettings?.recurrentEvents,
        values?.name,
        values?.eventSchedule?.eventStartUtc,
        values?.eventSchedule?.eventEndUtc,
        values?.eventSchedule?.registrationStartUtc,
        values?.eventSchedule?.registrationEndUtc,
        selectedTimeZoneValue,
    ]);

    const isScheduleSingleDay = isSingleDayEvent({
        eventStartUtc: values?.eventSchedule?.eventStartUtc,
        eventEndUtc: values?.eventSchedule?.eventEndUtc,
        timezoneValue: selectedTimeZoneValue,
    });

    // Compute the maximum "Ends On" date for recurring events based on max occurrences and cadence
    const maxEndsOnMoment = useMemo(() => {
        if (!isRecurring || !values?.eventSchedule || !selectedTimeZoneValue) { return null; }

        const tempSettings = {
            ...recurrenceSettings,
            numberOfOccurrences: RECURRENCE_MAX_INSTANCES,
            endsOnDate: null,
        };

        const result = generateRecurringScheduleInstances({
            name: values?.name,
            schedule: values?.eventSchedule,
            recurrenceSettings: tempSettings,
            timezoneValue: selectedTimeZoneValue,
            maxInstances: RECURRENCE_MAX_INSTANCES,
            matchRegistrationStartForAll: recurrenceSettings?.matchRegistrationStartForAll !== false,
        });

        const lastInstance = result?.instances?.[result.instances.length - 1];

        if (!lastInstance?.eventStartUtc) { return null; }

        return moment.utc(lastInstance.eventStartUtc).tz(selectedTimeZoneValue);
    }, [
        isRecurring,
        values?.eventSchedule,
        values?.name,
        selectedTimeZoneValue,
        recurrenceSettings?.cadenceId,
        recurrenceSettings?.cadenceDayOfWeek,
        recurrenceSettings?.cadenceDayOfMonth,
        recurrenceSettings?.matchRegistrationStartForAll,
    ]);

    const mergeInstancesWithOverrides = (generated, existing = []) => {
        return generated.map((instance, i) => {
            const prev = existing[i];

            if (prev?.isNameOverridden) {
                return {
                    ...instance,
                    name: prev.name,
                    isNameOverridden: true,
                };
            }

            return instance;
        });
    };

    const generateRecurrenceInstances = () => {
        if (!isRecurring || !values?.eventSchedule || !recurrenceSettings || !selectedTimeZoneValue) {
            setRecurrenceInstances([]);
            return;
        }

        const result = generateRecurringScheduleInstances({
            name: values?.name,
            schedule: values?.eventSchedule,
            recurrenceSettings,
            timezoneValue: selectedTimeZoneValue,
            maxInstances: RECURRENCE_MAX_INSTANCES,
            matchRegistrationStartForAll: recurrenceSettings?.matchRegistrationStartForAll !== false,
        });

        setRecurrenceInstances(prev => {
            const generated = result?.instances || [];
            return mergeInstancesWithOverrides(generated, prev);
        });
    };

    useEffect(() => {
        // Do not regenerate instances while editing (preserve API data)
        if (editing) { return; }

        generateRecurrenceInstances();
    }, [
        isRecurring,
        selectedTimeZoneValue,
        values?.eventSchedule?.eventStartUtc,
        values?.eventSchedule?.eventEndUtc,
        values?.eventSchedule?.registrationStartUtc,
        values?.eventSchedule?.registrationEndUtc,
        recurrenceSettings?.cadenceId,
        recurrenceSettings?.cadenceDayOfWeek,
        recurrenceSettings?.cadenceDayOfMonth,
        recurrenceSettings?.numberOfOccurrences,
        recurrenceSettings?.endsOnDate,
        recurrenceSettings?.matchRegistrationStartForAll,
        editing,
    ]);

    // Keep the primary instance in sync with the main event name and times
    useEffect(() => {
        if (editing || !isRecurring || recurrenceInstances.length === 0) { return; }

        setRecurrenceInstances(prev => {
            const next = [...prev];
            const primary = next[0];

            if (!primary) { return prev; }

            const updatedPrimary = {
                ...primary,
                name: values?.name,
                eventStartUtc: values?.eventSchedule?.eventStartUtc,
                eventEndUtc: values?.eventSchedule?.eventEndUtc,
            };

            if (
                primary.name === updatedPrimary.name &&
                primary.eventStartUtc === updatedPrimary.eventStartUtc &&
                primary.eventEndUtc === updatedPrimary.eventEndUtc
            ) {
                return prev;
            }

            next[0] = updatedPrimary;
            return next;
        });
    }, [
        isRecurring,
        values?.name,
        values?.eventSchedule?.eventStartUtc,
        values?.eventSchedule?.eventEndUtc,
        editing,
    ]);

    useEffect(() => {
        // Do not overwrite API-loaded recurrentEvents while editing
        if (editing || !isRecurring) { return; }

        const recurrentEvents = recurrenceInstances
            // exclude the primary event from the recurrent events list since it is represented by the main event fields
            .filter((instance) => instance.order !== 1)
            .map((instance) => ({
                id: instance.id || 0,
                name: instance.name,
                order: instance.order,
                startDate: moment.utc(instance.eventStartUtc).toISOString(),
                endDate: moment.utc(instance.eventEndUtc).toISOString(),
                registrationStartDate: moment.utc(instance.registrationStartUtc).toISOString(),
                registrationEndDate: moment.utc(instance.registrationEndUtc).toISOString(),
            }));

        formApi.setValue('recurrenceSettings.recurrentEvents', recurrentEvents);
    }, [isRecurring, recurrenceInstances, editing]);

    // propagate primary event name to recurring events that were not renamed
    useEffect(() => {
        // Do not propagate/overwrite names while editing
        if (editing || !isRecurring || recurrenceInstances.length === 0) {
            return;
        }

        setRecurrenceInstances(prev => prev.map((instance, index) => {
            if (index === 0) { return instance; } // skip primary
            if (instance.isNameOverridden) { return instance; }

            return {
                ...instance,
                name: values?.name,
            };
        }));
    }, [values?.name, isRecurring, editing]);

    // Lock recurrence settings once any event in the series has been published.
    // A recurring event is published when it has a tmsEventId (created in TMS).
    // If the series is still fully draft, allow editing recurrence settings even when editing.
    const isAnySeriesEventPublished = editing && isRecurring && (
        !!values?.published ||
        (values?.recurrenceSettings?.recurrentEvents || []).some(e => !!e.tmsEventId)
    );
    const isRecurringSettingsLocked = isAnySeriesEventPublished;
    const isRecurringSelectionDisabled = editing && !isRecurring;
    const maxEventEndUtc = values?.eventSchedule?.maxEventEndUtc;
    const { $tourDateStart, $tourDateEnd, $regDateStart, $regDateEnd, $drawsDate, $maxTourDateEnd } = schedule;

    const setRecurrenceSettings = (nextPartial) => {
        const nextValue = {
            ...(values.recurrenceSettings || defaultRecurrenceSettings),
            ...nextPartial,
        };
        formApi.setValue('recurrenceSettings', nextValue);
    };

    const ensureCadenceSpecificFields = (cadenceKey) => {
        const baseStart = moment.utc(values?.eventSchedule?.eventStartUtc);
        const cadenceId = mapFrontendCadenceToId(cadenceKey);
        const nextPartial = {
            cadenceId,
            cadenceDayOfWeek: null,
            cadenceDayOfMonth: null,
        };
        if (cadenceId === CADENCE_IDS.WEEKLY) {
            nextPartial.cadenceDayOfWeek = mapMomentDayToBackendDay(baseStart);
        }
        if (cadenceKey === RECURRENCE_CADENCE.MONTHLY) {
            nextPartial.cadenceDayOfMonth = values?.eventSchedule?.eventStartUtc;
        }
        return nextPartial;
    };

    const handleDateChange = (key, date, fieldApi) => {
        const newSchedule = { ...schedule };
        newSchedule[`$${key}`] = date;

        if (newSchedule?.$tourDateEnd?.isBefore(newSchedule.$regDateEnd)) {
            newSchedule.$regDateEnd.set({
                year: newSchedule.$tourDateEnd.year(),
                month: newSchedule.$tourDateEnd.month(),
                date: newSchedule.$tourDateEnd.date(),
            });
            fieldApi.setValue('registrationEndUtc', newSchedule.$regDateEnd.toISOString());
        }
        if (newSchedule?.$tourDateStart?.isBefore(newSchedule.$regDateStart)) {
            newSchedule.$regDateStart.set({
                year: newSchedule.$tourDateStart.year(),
                month: newSchedule.$tourDateStart.month(),
                date: newSchedule.$tourDateStart.date(),
            });
            fieldApi.setValue('registrationStartUtc', newSchedule.$regDateStart.toISOString());
        }
        if (newSchedule?.$tourDateStart?.isBefore(newSchedule.$drawsDate)) {
            newSchedule.$drawsDate.set({
                year: newSchedule.$tourDateStart.year(),
                month: newSchedule.$tourDateStart.month(),
                date: newSchedule.$tourDateStart.date(),
            });
            fieldApi.setValue('drawPublishedUtc', newSchedule.$drawsDate.toISOString());
        }
        if (newSchedule?.$tourDateStart?.isAfter(newSchedule.$tourDateEnd)) {
            newSchedule.$tourDateEnd.set({
                year: newSchedule.$tourDateStart.year(),
                month: newSchedule.$tourDateStart.month(),
                date: newSchedule.$tourDateStart.date(),
            });
            fieldApi.setValue('eventEndUtc', newSchedule.$tourDateEnd.toISOString());
        }
        if (newSchedule?.$regDateStart?.isAfter(newSchedule.$regDateEnd)) {
            newSchedule.$regDateEnd.set({
                year: newSchedule.$regDateStart.year(),
                month: newSchedule.$regDateStart.month(),
                date: newSchedule.$regDateStart.date(),
            });
            fieldApi.setValue('registrationEndUtc', newSchedule.$regDateEnd.toISOString());
        }

        fieldApi.validate(fieldApi.values);
        setSchedule(newSchedule);
    };

    const toggleTimezone = (show, val) => {
        setShowTimezone(show);
        if (val) {
            setTimezone(parseInt(val, 10));
            updateEventScheduleDatesAfterTimeZoneUpdate(formApi, coreUITimeZones, val);
            updateDivisionSpecificDatesAfterTimeZoneUpdate(formApi, coreUITimeZones, val);
        }
    };

    const toggleDrawDates = (e, setValue) => {
        if (showDrawDates) {
            setValue('drawPublishedUtc', null);
            setShowDrawDates(false);
            setSchedule(prev => ({ ...prev, $drawsDate: null }));
        } else {
            const timeZoneObj = timeZones.find(tz => tz.id === parseInt(timezone, 10));
            const nowTopOfNextHour = moment.utc().add(1, 'hour').startOf('hour').tz(timeZoneObj.value);
            setValue('drawPublishedUtc', nowTopOfNextHour.toISOString());
            setShowDrawDates(true);
            setSchedule(prev => ({ ...prev, $drawsDate: nowTopOfNextHour }));
        }
    };

    const scheduleValidate = (schedule) => scheduleValidateUtil(schedule, {
        values,
        isRecurring,
        recurrenceCadenceKey,
        isScheduleSingleDay,
        clubSettings,
        recurrenceSettings,
        recurrenceInstances,
    });

    const eventDurationDays = useMemo(() => {
        const start = values?.eventSchedule?.eventStartUtc;
        const end = values?.eventSchedule?.eventEndUtc;

        if (!start || !end) { return null; }

        const startMoment = moment.utc(start);
        const endMoment = moment.utc(end);

        return endMoment.diff(startMoment, 'days');
    }, [
        values?.eventSchedule?.eventStartUtc,
        values?.eventSchedule?.eventEndUtc,
    ]);

    const cadenceOptions = useMemo(() => {
        const baseOptions = getCadenceOptions({ includeDaily: isScheduleSingleDay });

        if (!eventDurationDays && eventDurationDays !== 0) {
            return baseOptions;
        }

        return baseOptions.filter(option => {
            const key = option.id;

            // Daily already handled via includeDaily
            if (key === RECURRENCE_CADENCE.WEEKLY && eventDurationDays >= 7) {
                return false;
            }

            if (key === RECURRENCE_CADENCE.MONTHLY && eventDurationDays >= 30) {
                return false;
            }

            return true;
        });
    }, [isScheduleSingleDay, eventDurationDays]);

    // When event duration changes and the selected cadence is no longer a valid option
    // (e.g. weekly filtered out because event > 7 days), auto-select the first available cadence
    // so the Select doesn't appear blank and instances aren't generated with a stale cadence.
    useEffect(() => {
        if (editing || !isRecurring || !recurrenceCadenceKey) { return; }
        const isCurrentCadenceValid = cadenceOptions.some(o => o.id === recurrenceCadenceKey);
        if (!isCurrentCadenceValid && cadenceOptions.length > 0) {
            const nextValues = ensureCadenceSpecificFields(cadenceOptions[0].id);
            setRecurrenceSettings(nextValues);
        }
    }, [cadenceOptions]);

    const occurrencesOptions = Array.from({ length: RECURRENCE_MAX_INSTANCES - 1 }, (_, index) => {
        const value = index + 2;
        return {
            id: value,
            label: `${value} Occurrences`,
        };
    });

    const formatInstanceSchedule = (instance) => formatInstanceScheduleUtil(instance, selectedTimeZoneValue);

    const startEditingInstance = (instance) => {
        setEditingInstanceIndex(instance.index);
        setInstanceEditorError(null);
        setInstanceEditorValues({
            name: instance.name || '',
            eventStartUtc: moment.utc(instance.eventStartUtc).tz(selectedTimeZoneValue),
            eventEndUtc: moment.utc(instance.eventEndUtc).tz(selectedTimeZoneValue),
            registrationStartUtc: moment.utc(instance.registrationStartUtc).tz(selectedTimeZoneValue),
            registrationEndUtc: moment.utc(instance.registrationEndUtc).tz(selectedTimeZoneValue),
        });
    };

    const saveInstanceEdit = (instance) => {
        const eventStartUtc = instanceEditorValues.eventStartUtc?.clone()?.toISOString();
        const eventEndUtc = instanceEditorValues.eventEndUtc?.clone()?.toISOString();
        const registrationStartUtc = instanceEditorValues.registrationStartUtc?.clone()?.toISOString();
        const registrationEndUtc = instanceEditorValues.registrationEndUtc?.clone()?.toISOString();
        const name = instanceEditorValues.name?.trim();
        if (!name) {
            setInstanceEditorError('Please provide an event title.');
            return;
        }
        if (!eventStartUtc || !eventEndUtc || !registrationStartUtc || !registrationEndUtc) {
            setInstanceEditorError('Please provide all occurrence dates before saving.');
            return;
        }
        if (moment.utc(eventEndUtc).isSameOrBefore(moment.utc(eventStartUtc))) {
            setInstanceEditorError('The event end date must be after the event begins date.');
            return;
        }
        if (moment.utc(registrationStartUtc).isSameOrAfter(moment.utc(registrationEndUtc))) {
            setInstanceEditorError('Registration start must be before registration close.');
            return;
        }
        if (moment.utc(registrationEndUtc).isSameOrAfter(moment.utc(eventEndUtc))) {
            setInstanceEditorError('Registration close must be before the event end date.');
            return;
        }

        setRecurrenceInstances((prev) => prev.map((i) => (
            i.order === instance.order
                ? {
                    ...i,
                    name,
                    isNameOverridden: name !== values?.name,
                    eventStartUtc,
                    eventEndUtc,
                    registrationStartUtc,
                    registrationEndUtc,
                }
                : i
        )));
        // Trigger form validation after manual edit
        if (formApi?.validate) {
            setTimeout(() => formApi.validate(), 0);
        }
        setEditingInstanceIndex(null);
        setInstanceEditorError(null);
    };

    const cancelInstanceEdit = () => {
        setEditingInstanceIndex(null);
        setInstanceEditorError(null);
    };

    const resetRecurringValues = () => {
        setRecurrenceInstances([]);
        setEditingInstanceIndex(null);
        setInstanceEditorError(null);
        formApi.setValue('recurrenceSettings', defaultRecurrenceSettings);
    };

    const recurrenceValidation = validateRecurringScheduleInstances(
        recurrenceInstances,
        RECURRENCE_MAX_INSTANCES,
    );

    // Reactive recurrence error — always derived from current state so it clears
    // immediately when cadence/instances change, without relying on the stale
    // NestedField error (which only updates when the schedule field value changes).
    const liveRecurrenceError = useMemo(() => {
        if (!isRecurring) { return null; }
        if (recurrenceCadenceKey === RECURRENCE_CADENCE.DAILY && !isScheduleSingleDay) {
            return 'Daily recurrence is only available for single-day events.';
        }
        if ((!recurrenceSettings?.numberOfOccurrences || recurrenceSettings.numberOfOccurrences < 1) && !recurrenceSettings?.endsOnDate) {
            return 'Please select how many occurrences to create.';
        }
        if (recurrenceValidation?.errors?.length) {
            return recurrenceValidation.errors.join(' ');
        }
        return null;
    }, [isRecurring, recurrenceCadenceKey, isScheduleSingleDay, recurrenceSettings?.numberOfOccurrences, recurrenceSettings?.endsOnDate, recurrenceValidation]);

    const timeZoneComponent = (
        <>
            <p className="text-small mt24 text-center" data-testid="event-edit.schedule.p-628"><strong>Timezone: </strong>
                {(timezone ? timeZones.find(tz => tz.id === parseInt(timezone, 10)).label : '')}
                <a
                    href="#"
                    className="underline"
                    onClick={(e) => {
                        e.preventDefault();
                        toggleTimezone(true);
                    }}
                    data-testid="event-edit.schedule.timezone-change-link"
                >
                    change
                </a>
            </p>
            {showTimezone ? (
                <div className="row">
                    <div className="col-md-8">
                        <Select
                            id="timezone"
                            hiddenLabel={true}
                            hiddenDefault={true}
                            field="timeZone"
                            options={timeZones}
                            onChange={(val) => toggleTimezone(false, val)}
                            data-testid="event-edit.schedule.timezone-select"
                        />
                    </div>
                </div>
            ) : null}
        </>
    );

    const editingInstance = recurrenceInstances.find(instance => instance.index === editingInstanceIndex);
    const editingInstanceTitle = `Update Event Title, Date or Times`;
    const editInstanceModalBody = editingInstance ? (
        <div className={styles.scheduleInstancePopupBody}>
            <div className="row">
                <div className="col-12">
                    <Input
                        key={`recurrenceEditName-${editingInstance.index}`}
                        controlled
                        label="Event Title"
                        id={`recurrenceEditName-${editingInstance.index}`}
                        type="text"
                        placeholder="Event Title"
                        data-testid={`event-edit.schedule.recurrence-${editingInstance.index}.title-input`}
                        value={instanceEditorValues.name || ''}
                        validate={required}
                        onChange={(e) => {
                            const value = e?.target?.value ?? '';
                            setInstanceEditorValues(prev => ({ ...prev, name: value }));
                        }}
                        themeV3
                    />
                </div>
                <div className="col-md-6">
                    <label className="text-dark mt16" data-testid="event-edit.schedule.label-684">Event Starts</label>
                    <DateTimePicker
                        id={`recurrenceEditStart-${editingInstance.index}`}
                        field={`recurrenceEditor.${editingInstance.index}.eventStartUtc`}
                        selected={instanceEditorValues.eventStartUtc || moment()}
                        minDate={moment().subtract(365, 'days')}
                        maxDate={null}
                        handleDateChange={(date) => setInstanceEditorValues(prev => ({ ...prev, eventStartUtc: date }))}
                        themeV3
                        isStacked
                        popperContainer={RecurrenceDatePickerPortal}
                        popperClassName={styles.recurrenceDatePickerPopper}
                        data-testid="event-edit.schedule.recurrence-edit-start-item-date-picker"
                    />
                </div>
                <div className="col-md-6">
                    <label className="text-dark mt16" data-testid="event-edit.schedule.label-699">Event Ends</label>
                    <DateTimePicker
                        id={`recurrenceEditEnd-${editingInstance.index}`}
                        field={`recurrenceEditor.${editingInstance.index}.eventEndUtc`}
                        selected={instanceEditorValues.eventEndUtc || moment()}
                        minDate={instanceEditorValues.eventStartUtc || moment()}
                        maxDate={null}
                        handleDateChange={(date) => setInstanceEditorValues(prev => ({ ...prev, eventEndUtc: date }))}
                        themeV3
                        isStacked
                        popperContainer={RecurrenceDatePickerPortal}
                        popperClassName={styles.recurrenceDatePickerPopper}
                        data-testid="event-edit.schedule.recurrence-edit-end-item-date-picker"
                    />
                </div>
            </div>
            <div className="div div16 mb0 mt8" />
            <div className="row">
                <div className="col-md-6">
                    <label className="text-dark mt16" data-testid="event-edit.schedule.label-717">Registration Starts</label>
                    <DateTimePicker
                        id={`recurrenceEditRegStart-${editingInstance.index}`}
                        field={`recurrenceEditor.${editingInstance.index}.registrationStartUtc`}
                        selected={instanceEditorValues.registrationStartUtc || moment()}
                        minDate={moment().subtract(365, 'days')}
                        maxDate={instanceEditorValues.eventEndUtc || moment().add(1, 'year')}
                        handleDateChange={(date) => setInstanceEditorValues(prev => ({ ...prev, registrationStartUtc: date }))}
                        themeV3
                        isStacked
                        popperContainer={RecurrenceDatePickerPortal}
                        popperClassName={styles.recurrenceDatePickerPopper}
                        data-testid="event-edit.schedule.recurrence-edit-reg-start-item-date-picker"
                    />
                </div>
                <div className="col-md-6">
                    <label className="text-dark mt16" data-testid="event-edit.schedule.label-732">Registration Ends</label>
                    <DateTimePicker
                        id={`recurrenceEditRegEnd-${editingInstance.index}`}
                        field={`recurrenceEditor.${editingInstance.index}.registrationEndUtc`}
                        selected={instanceEditorValues.registrationEndUtc || moment()}
                        minDate={moment().subtract(365, 'days')}
                        maxDate={instanceEditorValues.eventEndUtc || moment().add(1, 'year')}
                        handleDateChange={(date) => setInstanceEditorValues(prev => ({ ...prev, registrationEndUtc: date }))}
                        themeV3
                        isStacked
                        popperContainer={RecurrenceDatePickerPortal}
                        popperClassName={styles.recurrenceDatePickerPopper}
                        data-testid="event-edit.schedule.recurrence-edit-reg-end-item-date-picker"
                    />
                </div>
            </div>
            {instanceEditorError && (
                <div className="is-invalid text-center mb16">
                    <div className="invalid-feedback">{instanceEditorError}</div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="col-12">
            <Tip
                variant="info"
                dangerouslySetInnerHTML={true}
                valign="top"
                content="<b>Pro Tip:</b> If players want to register after registration has closed, edit your event and reopen registration here so they can join."
            >
                <div className="form-block-content">
                    <NestedField field={field} validateOnSubmit={false} validate={scheduleValidate}>
                        {(fieldApi) => {
                            const { error, setValue } = fieldApi;
                            return (
                                <>
                                    <div className={styles.scheduleBlock}>
                                        {isRecurringEventsEnabled && (
                                            <>
                                                <div className={styles.scheduleRecurringMode}>
                                                    <div className={styles.scheduleRecurringModeOptions}>
                                                        <label className={styles.scheduleRecurringRadio} data-testid="event-edit.schedule.label-774">
                                                            <input
                                                                type="radio"
                                                                checked={!isRecurring}
                                                                disabled={isRecurringSettingsLocked}
                                                                onChange={() => {
                                                                    formApi.setValue('recurrenceSettings', null);
                                                                    setRecurrenceInstances([]);
                                                                }}
                                                                data-testid="event-edit.schedule.single-event-radio"
                                                            />
                                                            <span data-testid="event-edit.schedule.span-785">Single Event</span>
                                                        </label>
                                                        <label className={styles.scheduleRecurringRadio} data-testid="event-edit.schedule.label-787">
                                                            <input
                                                                type="radio"
                                                                checked={isRecurring}
                                                                disabled={isRecurringSelectionDisabled || isRecurringSettingsLocked}
                                                                onChange={() => {
                                                                    const firstValidCadence = cadenceOptions[0]?.id ?? RECURRENCE_CADENCE.MONTHLY;
                                                                    const cadenceDefaults = ensureCadenceSpecificFields(firstValidCadence);
                                                                    formApi.setValue('recurrenceSettings', {
                                                                        ...defaultRecurrenceSettings,
                                                                        ...cadenceDefaults,
                                                                    });
                                                                    setRecurrenceInstances([]);
                                                                }}
                                                                data-testid="event-edit.schedule.recurring-event-radio"
                                                            />
                                                            <span data-testid="event-edit.schedule.span-803">Recurring Event</span>
                                                        </label>
                                                    </div>
                                                    {isRecurring && !isRecurringSettingsLocked && (
                                                        <div className={`clickable ${styles.scheduleResetLink}`} onClick={resetRecurringValues} data-testid="event-edit.schedule.div-807">Reset</div>
                                                    )}
                                                </div>
                                                <div className="div div16 mt16" />
                                            </>
                                        )}

                                        <div className="row">
                                            <div className="col-md-6">
                                                <label className="text-dark mt16" data-testid="event-edit.schedule.label-816">Event Starts</label>
                                                <DateTimePicker
                                                    id="tourDateStart"
                                                    field="eventStartUtc"
                                                    data-testid="event-edit.schedule.start-date"
                                                    selected={$tourDateStart}
                                                    minDate={$tourDateStart?.isBefore(moment()) ? $tourDateStart.clone().startOf('minute') : moment().startOf('minute')}
                                                    maxDate={null}
                                                    handleDateChange={(date) => handleDateChange('tourDateStart', date, fieldApi)}
                                                    validate={required}
                                                    themeV3
                                                    isStacked
                                                />
                                            </div>
                                            <div className={`col-md-6 ${error?.eventEnd ? 'is-invalid' : ''}`}>
                                                <label className="text-dark mt16 d-flex aic" data-testid="event-edit.schedule.label-831">
                                                    Event Ends {!!maxEventEndUtc && (<InfoButton hoverText={'Once in progress, Verified events cannot be extended beyond a week from the original end date.'} />)}
                                                </label>
                                                <DateTimePicker
                                                    id="tourDateEnd"
                                                    field="eventEndUtc"
                                                    data-testid="event-edit.schedule.end-date"
                                                    selected={$tourDateEnd}
                                                    minDate={$tourDateStart}
                                                    maxDate={$maxTourDateEnd}
                                                    handleDateChange={(date) => handleDateChange('tourDateEnd', date, fieldApi)}
                                                    validate={required}
                                                    themeV3
                                                    isStacked
                                                />
                                            </div>
                                        </div>

                                        <div className="div mt24" />
                                        <div className="row">
                                            <div className={`col-md-6 ${error?.regStart ? 'is-invalid' : ''}`}>
                                                <label className="text-dark mt16" data-testid="event-edit.schedule.label-854">Registration Starts</label>
                                                <DateTimePicker
                                                    id="regDateStart"
                                                    field="registrationStartUtc"
                                                    data-testid="event-edit.schedule.reg-start"
                                                    selected={$regDateStart}
                                                    maxDate={$tourDateStart}
                                                    handleDateChange={(date) => handleDateChange('regDateStart', date, fieldApi)}
                                                    validate={required}
                                                    themeV3
                                                    isStacked
                                                />
                                            </div>
                                            <div className={`col-md-6 ${error?.regEnd ? 'is-invalid' : ''}`}>
                                                <label className="text-dark mt16" data-testid="event-edit.schedule.label-868">Registration Ends</label>
                                                <DateTimePicker
                                                    id="regDateEnd"
                                                    field="registrationEndUtc"
                                                    data-testid="event-edit.schedule.reg-end"
                                                    selected={$regDateEnd}
                                                    minDate={$regDateStart}
                                                    maxDate={$tourDateEnd}
                                                    handleDateChange={(date) => handleDateChange('regDateEnd', date, fieldApi)}
                                                    validate={required}
                                                    themeV3
                                                    isStacked
                                                />
                                            </div>
                                        </div>

                                        {!isHighSchoolClub && timeZoneComponent}

                                        {isRecurring && isRecurringEventsEnabled && (
                                            <>
                                                <div className="div div16 mt16" />
                                                <div className={styles.scheduleRepeatSection}>
                                                    <div className={styles.sectionTitle} data-testid="event-edit.schedule.div-892">Repeat This Event</div>
                                                    <div className="mt24">
                                                        <Select
                                                            controlled
                                                            id="recurrenceCadence"
                                                            label="Cadence"
                                                            options={cadenceOptions}
                                                            value={recurrenceCadenceKey || ''}
                                                            disabled={isRecurringSettingsLocked}
                                                            onChange={(cadenceKey) => {
                                                                const nextValues = ensureCadenceSpecificFields(cadenceKey);
                                                                setRecurrenceSettings(nextValues);
                                                            }}
                                                            themeV3
                                                            data-testid="event-edit.schedule.recurrence-cadence-select"
                                                        />
                                                    </div>

                                                    <div className={styles.scheduleEndsMode}>
                                                        <div className={styles.scheduleEndsItem}>
                                                            <label className={styles.scheduleRecurringRadio} data-testid="event-edit.schedule.label-913">
                                                                <input
                                                                    type="radio"
                                                                    data-testid="event-edit.schedule.recurrence-ends-after-radio"
                                                                    checked={recurrenceEndsMode === RECURRENCE_END_OPTIONS.AFTER}
                                                                    disabled={isRecurringSettingsLocked}
                                                                    onChange={() => {
                                                                        setRecurrenceEndsMode(RECURRENCE_END_OPTIONS.AFTER);
                                                                        setRecurrenceSettings({ endsOnDate: null, numberOfOccurrences: recurrenceSettings?.numberOfOccurrences || 6 });
                                                                    }}
                                                                />
                                                            </label>
                                                            <Select
                                                                controlled
                                                                id="recurrenceNumberOfOccurrences"
                                                                label="Ends After"
                                                                options={occurrencesOptions}
                                                                value={recurrenceSettings?.numberOfOccurrences || 1}
                                                                disabled={recurrenceEndsMode !== RECURRENCE_END_OPTIONS.AFTER || isRecurringSettingsLocked}
                                                                onChange={(value) => {
                                                                    const numberOfOccurrences = parseInt(value, 10);
                                                                    const nextSetting = {
                                                                        numberOfOccurrences,
                                                                        endsOnDate: null,
                                                                    };
                                                                    setRecurrenceSettings(nextSetting);
                                                                }}
                                                                themeV3
                                                                formGroupCssClass={`mb0`}
                                                                    data-testid="event-edit.schedule.recurrence-number-of-occurrences-select"
                                                            />
                                                        </div>
                                                        <div className={styles.scheduleEndsOr} data-testid="event-edit.schedule.div-945">Or</div>
                                                        <div className={styles.scheduleEndsItem}>
                                                            <label className={styles.scheduleRecurringRadio} data-testid="event-edit.schedule.label-947">
                                                                <input
                                                                    type="radio"
                                                                    data-testid="event-edit.schedule.recurrence-ends-on-radio"
                                                                    checked={recurrenceEndsMode === RECURRENCE_END_OPTIONS.ON}
                                                                    disabled={isRecurringSettingsLocked}
                                                                    onChange={() => {
                                                                        const endsOnDate = ($tourDateStart || moment());
                                                                        setRecurrenceEndsMode(RECURRENCE_END_OPTIONS.ON);
                                                                        setRecurrenceSettings({
                                                                            endsOnDate, numberOfOccurrences: null,
                                                                        });
                                                                    }}
                                                                />
                                                            </label>
                                                            <DateTimePicker
                                                                id="recurrenceEndsOnDate"
                                                                field="recurrenceSettings.endsOnDate"
                                                                selected={recurrenceSettings?.endsOnDate
                                                                    ? moment.utc(recurrenceSettings.endsOnDate).tz(selectedTimeZoneValue)
                                                                    : ($tourDateStart || moment())}
                                                                minDate={$tourDateStart || moment()}
                                                                maxDate={maxEndsOnMoment || null}
                                                                hideTimePicker
                                                                dateLabel="Ends On"
                                                                timeLabel={null}
                                                                disabled={recurrenceEndsMode !== RECURRENCE_END_OPTIONS.ON || isRecurringSettingsLocked}
                                                                handleDateChange={(date) => {
                                                                    let nextMoment = date ? date.clone().startOf('day') : null;

                                                                    if (nextMoment && maxEndsOnMoment && nextMoment.isAfter(maxEndsOnMoment)) {
                                                                        nextMoment = maxEndsOnMoment.clone();
                                                                    }

                                                                    const nextDate = nextMoment ? nextMoment.utc().toISOString() : null;
                                                                    const nextSetting = {
                                                                        endsOnDate: nextDate,
                                                                        numberOfOccurrences: null,
                                                                    };
                                                                    setRecurrenceSettings(nextSetting);
                                                                }}
                                                                themeV3
                                                                            data-testid="event-edit.schedule.recurrence-ends-on-date-date-picker"
                                                            />
                                                        </div>
                                                    </div>

                                                </div>
                                            </>
                                        )}

                                        {isRecurring && isRecurringEventsEnabled && recurrenceInstances.length > 0 ? (
                                            <div className={`${styles.scheduleSummary} mt24`}>
                                                <div className={`${styles.sectionTitle} mb8`} data-testid="event-edit.schedule.div-1000">
                                                    Recurring Event Summary - {recurrenceInstances.length} Events
                                                </div>
                                                {recurrenceInstances.map((instance, index) => {
                                                    const formatted = formatInstanceSchedule(instance);
                                                    return (
                                                        <div key={instance.index} className={`${styles.scheduleSummaryRow}`}>
                                                            <div className={`${styles.scheduleSummaryMain} ${index === 0 ? styles.isPrimary : ''}`}>
                                                                <div className={`${styles.scheduleSummaryTitle}`}>{ index === 0 ? 'Primary' : `Event ${index + 1}`}</div>
                                                                <div className={styles.scheduleSummaryDetails}>
                                                                    <p data-testid="event-edit.schedule.p-1010">{index === 0 ? values.name : instance.name}</p>
                                                                    <p data-testid="event-edit.schedule.p-1011">{formatted.eventDate}</p>
                                                                    <p className="text-small" data-testid="event-edit.schedule.p-1012">{formatted.regDate}</p>
                                                                </div>
                                                            </div>
                                                            {index > 0 && !isRecurringSettingsLocked && (
                                                                <div className={styles.scheduleSummaryActions}>
                                                                    <div className="clickable p8" onClick={() => startEditingInstance(instance)}>
                                                                        <Img src="/img/v4/icon-edit.svg" alt="Edit occurrence" />
                                                                    </div>
                                                                    <div
                                                                        className="clickable p8"
                                                                        onClick={() => {
                                                                            setPendingDeleteInstance(instance);
                                                                        }}
                                                                    >
                                                                        <Img src="/img/v4/icon-close-circle.svg" alt="Delete occurrence" />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {!!liveRecurrenceError && (
                                                    <div className="is-invalid text-center mb16">
                                                        <div className="invalid-feedback">
                                                            {liveRecurrenceError}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className={styles.scheduleRecurringBadgeToggle}>
                                                    <TitleSwitch
                                                        id="matchRegistrationStartForAll"
                                                        label="Match registration start date and time for all recurring events"
                                                        value={recurrenceSettings?.matchRegistrationStartForAll !== false}
                                                        onChange={(e) => {
                                                            setRecurrenceSettings({
                                                                matchRegistrationStartForAll: e?.target?.checked ?? true,
                                                            });
                                                        }}
                                                        disabled={isRecurringSettingsLocked}
                                                            data-testid="event-edit.match-registration-start-for-all-title-switch"
                                                    />

                                                    <TitleSwitch
                                                        id="displayRecurrentEventBadge"
                                                        label="Show as recurring events: Display a recurring label and promote the rest of the series on event pages and to players right after they register"
                                                        value={!!recurrenceSettings?.displayRecurrentEventBadge}
                                                        onChange={(e) => {
                                                            setRecurrenceSettings({
                                                                displayRecurrentEventBadge: e?.target?.checked ?? false,
                                                            });
                                                        }}
                                                        disabled={isRecurringSettingsLocked}
                                                                    data-testid="event-edit.schedule.match-registration-start-for-all-title-switch"
                                                    />
                                                </div>

                                                <div className="text-small text-muted mt16">
                                                    <p className="mb8" data-testid="event-edit.schedule.p-1071"><strong>Note:</strong></p>
                                                    <ul className="pl16 mb0">
                                                        <li>The registration window you set for the primary event will also be used for all recurring events.</li>
                                                        <li>After setting up your event, you can publish it (including all recurrences) or save it as a draft. You can access and edit it anytime from your Club page.</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {isRecurring && isRecurringEventsEnabled && !!liveRecurrenceError && (
                                                    <div className="is-invalid text-center mb16">
                                                        <div className="invalid-feedback">
                                                            {liveRecurrenceError}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {values.eventType === 1 && (
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="div div16 mt16" />
                                                <TitleSwitch
                                                    id="publishDates"
                                                    formGroupCssClass="mt16"
                                                    label="Draw Publishing Dates"
                                                    value={showDrawDates}
                                                    onChange={(e) => toggleDrawDates(e, setValue)}
                                                    data-testid="event-edit.schedule.publish-dates-toggle"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {showDrawDates && (
                                        <div className="row">
                                            <div className={`col-md-12 ${error?.drawPost ? 'is-invalid' : ''}`}>
                                                <div className="bg-white p16">
                                                    <label className="text-dark mb16" data-testid="event-edit.schedule.label-1111">
                                                        Draws Published (Optional)
                                                        <InfoButton hoverText="Event organizers commit to posting completed draws by this date and time. Draws will not automatically publish at this time. Organizers must take action to publish the draws prior to time chosen." />
                                                    </label>
                                                    <DateTimePicker
                                                        id="drawsDate"
                                                        field="drawPublishedUtc"
                                                        selected={$drawsDate}
                                                        minDate={moment()}
                                                        maxDate={$tourDateStart}
                                                        handleDateChange={(date) => handleDateChange('drawsDate', date, fieldApi)}
                                                        themeV3
                                                            data-testid="event-edit.schedule.publish-dates-title-switch"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isHighSchoolClub && timeZoneComponent}
                                    {error?.schedule ? <Message error={error.schedule} /> : null}
                                    {shouldCheckForEventDuration(values, values?.eventSchedule) && (
                                        <div className="is-invalid text-center mb16">
                                            <div className="invalid-feedback">
                                                <LinkAnchor useAnchor link="https://utrsports.net/verified" target="_blank">Verified Events</LinkAnchor>
                                                {` can run for a maximum of ${maxVerifiedEventDuration} days.`}
                                            </div>
                                        </div>
                                    )}
                                    <PopupV3
                                        title={editingInstanceTitle}
                                        message={editInstanceModalBody}
                                        type={PopupTypes.INFO}
                                        isOpen={!!editingInstance}
                                        onConfirmClick={() => saveInstanceEdit(editingInstance)}
                                        onCancelClick={cancelInstanceEdit}
                                        showCancelButton
                                        cancelButtonLabel="Cancel"
                                        confirmButtonLabel="Save"
                                        customContent
                                        noIcon
                                        className={styles.scheduleInstancePopup}
                                        data-testid="event-edit.schedule.draws-date-date-picker"
                                    />
                                    <PopupV3
                                        title="Delete Recurring Event"
                                        message="Are you sure you want to delete this event? All other events in the recurring event series will keep their existing dates."
                                        type={PopupTypes.WARNING}
                                        isOpen={!!pendingDeleteInstance}
                                        onConfirmClick={() => {
                                            if (pendingDeleteInstance) {
                                                setRecurrenceInstances((prev) => prev.filter(i => i.order !== pendingDeleteInstance.order));
                                                // Trigger validation after deletion
                                                if (formApi?.validate) {
                                                    setTimeout(() => formApi.validate(), 0);
                                                }
                                            }
                                            setPendingDeleteInstance(null);
                                        }}
                                        onCancelClick={() => setPendingDeleteInstance(null)}
                                        showCancelButton
                                        cancelButtonLabel="Cancel"
                                        confirmButtonLabel="Delete"
                                        noIcon
                                    />
                                </>
                            );
                        }}
                    </NestedField>
                </div>
            </Tip>
        </div>
    );
}

Schedule.propTypes = {
    field: PropTypes.string.isRequired,
    formApi: PropTypes.objectOf(any).isRequired,
    isHighSchoolClub: PropTypes.bool,
    clubSettings: PropTypes.objectOf(any),
    editing: PropTypes.bool,
};

Schedule.defaultProps = {
    isHighSchoolClub: false,
    clubSettings: {},
    editing: false,
};

export default Schedule;
